(() => {
	// -----------------------------------------------------------
	// Mode detection: FULL | LITE
	// -----------------------------------------------------------
	const MODE = (() => {
		const q = new URLSearchParams(location.search);
		const force = (q.get('anim') || localStorage.getItem('hb-anim') || '').toLowerCase();
		if (force === 'lite') return 'lite';
		if (force === 'on' || force === 'full') return 'full';

		const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
		const saveData = !!(navigator.connection && navigator.connection.saveData);
		if (prefersReducedMotion || saveData) return 'lite';

		const mem = navigator.deviceMemory || 4;
		const cores = navigator.hardwareConcurrency || 4;
		const ua = navigator.userAgent || '';
		if (mem <= 4 || cores <= 4 || /OS (14|15)_\d/.test(ua) || /Android\s11\./i.test(ua)) return 'lite';

		return 'full';
	})();

	document.documentElement.classList.add(`hb-${MODE}`);

	// -----------------------------------------------------------
	// GSAP / ScrollTrigger
	// -----------------------------------------------------------
	const hasGSAP = !!window.gsap;
	if (hasGSAP && !window.ScrollTrigger && window.gsap?.registerPlugin) {
		try { window.gsap.registerPlugin(window.ScrollTrigger); } catch (_) { }
	}
	const gsap = window.gsap;
	const ScrollTrigger = window.ScrollTrigger;
	const canAnimate = !!gsap && !!ScrollTrigger;
	if (!canAnimate) {
		document.documentElement.classList.add('hb-no-anim');
		return;
	}

	// -----------------------------------------------------------
	// Utilities
	// -----------------------------------------------------------
	const $ = (s, sc = document) => sc.querySelector(s);
	const $$ = (s, sc = document) => Array.from(sc.querySelectorAll(s));

	const CFG = {
		reveal: MODE === 'lite'
			? { dy: 0, dur: 0.6, stagger: 0.06, ease: 'power1.out' }
			: { dy: 28, dur: 0.85, stagger: 0.09, ease: 'power2.out' },
	};

	// -----------------------------------------------------------
	// Helpers
	// -----------------------------------------------------------

	/** Content reveal — fade + rise (FULL) or fade-only (LITE). */
	function reveal(container, items, opts = {}) {
		if (!container) return;
		const targets = items ?? $$(
			'h1, h2, h3, p, span[class*="eyebrow"], .h-spritz__icon, .h-spritz__cta, .h-story__video-btn, .h-story__signature, .h-tasting__tagline',
			container
		);
		if (!targets.length) return;

		const { dy, dur, stagger, ease } = CFG.reveal;
		gsap.set(targets, { autoAlpha: 0, y: opts.dy ?? dy });
		ScrollTrigger.create({
			trigger: container,
			start: opts.start || 'top 82%',
			once: true,
			onEnter: () => gsap.to(targets, {
				autoAlpha: 1, y: 0, ease, duration: dur,
				stagger: opts.stagger ?? stagger,
			}),
		});
	}

	/** Arch image reveal — scale in on the container. */
	function revealArch(archContainer, opts = {}) {
		if (!archContainer) return;
		const { duration = MODE === 'lite' ? 0.5 : 0.7, start = 'top 85%' } = opts;
		gsap.set(archContainer, { autoAlpha: 0, scale: MODE === 'lite' ? 1 : 0.92 });
		ScrollTrigger.create({
			trigger: archContainer, start, once: true,
			onEnter: () => gsap.to(archContainer, {
				autoAlpha: 1, scale: 1, duration,
				ease: MODE === 'lite' ? 'power1.out' : 'power2.out',
			}),
		});
	}

	/** Fade-only — safe for elements with CSS transforms. */
	function fadeOnly(el, opts = {}) {
		if (!el) return;
		const { duration = MODE === 'lite' ? 0.5 : 0.9, delay = 0, trigger, start = 'top 85%' } = opts;
		gsap.set(el, { autoAlpha: 0 });
		ScrollTrigger.create({
			trigger: trigger || el, start, once: true,
			onEnter: () => gsap.to(el, { autoAlpha: 1, duration, delay, ease: 'power1.out' }),
		});
	}

	/** Button micro-interactions. */
	function enhanceButtons(selector) {
		$$(selector).forEach((btn) => {
			if (MODE === 'lite') {
				btn.addEventListener('mouseenter', () => gsap.to(btn, { y: -1, duration: 0.18, ease: 'power1.out' }));
				btn.addEventListener('mouseleave', () => gsap.to(btn, { y: 0, duration: 0.18, ease: 'power1.out' }));
			} else {
				btn.addEventListener('mouseenter', () => gsap.to(btn, { y: -2, scale: 1.015, duration: 0.25, ease: 'power2.out' }));
				btn.addEventListener('mouseleave', () => gsap.to(btn, { y: 0, scale: 1, duration: 0.25, ease: 'power2.out' }));
				btn.addEventListener('mousedown', () => gsap.to(btn, { scale: 0.985, duration: 0.12, ease: 'power1.out' }));
				btn.addEventListener('mouseup', () => gsap.to(btn, { scale: 1.015, duration: 0.12, ease: 'power1.out' }));
			}
		});
	}

	// ===========================================================
	// 1. HERO + INTRO EYEBROW (on-load sequence)
	//
	//    TRANSFORM SAFETY:
	//    - .h-hero__can → CSS translateX(-50%)
	//    - .h-hero__decoration--left → CSS translateX(-70%)
	//    - .h-hero__decoration--right → CSS translateX(40%)
	// ===========================================================
	const hero = $('.h-hero');
	const introEyebrow = $('.h-intro__eyebrow');
	const introScrolls = $$('.h-intro__scroll');

	if (hero) {
		const heroImg = $('.h-hero__img', hero);
		const heroCanImg = $('.h-hero__can-img', hero);
		const heroDecoL = $('.h-hero__decoration--left', hero);
		const heroDecoR = $('.h-hero__decoration--right', hero);

		// Hide eyebrow + scroll ornaments immediately so there's no flash
		if (introEyebrow) gsap.set(introEyebrow, { autoAlpha: 0, y: MODE === 'lite' ? 0 : 12 });
		if (introScrolls.length) gsap.set(introScrolls, { autoAlpha: 0 });

		if (MODE === 'full') {
			if (heroImg) {
				gsap.fromTo(heroImg,
					{ autoAlpha: 0, scale: 1.08 },
					{ autoAlpha: 1, scale: 1, duration: 2.6, ease: 'power4.out', delay: 0.1 }
				);
			}
			if (heroCanImg) {
				gsap.set(heroCanImg, { autoAlpha: 0, y: 40 });
				gsap.to(heroCanImg, {
					autoAlpha: 1, y: 0,
					duration: 1.2, ease: 'power3.out', delay: 0.3,
				});
			}
			if (heroDecoL) {
				gsap.set(heroDecoL, { autoAlpha: 0 });
				gsap.to(heroDecoL, { autoAlpha: 1, duration: 1.2, ease: 'power1.out', delay: 0.6 });
			}
			if (heroDecoR) {
				gsap.set(heroDecoR, { autoAlpha: 0 });
				gsap.to(heroDecoR, { autoAlpha: 1, duration: 1.2, ease: 'power1.out', delay: 0.7 });
			}
			// Eyebrow + scrolls fade in at the end of the hero sequence
			if (introEyebrow) {
				gsap.to(introEyebrow, { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power2.out', delay: 1.1 });
			}
			if (introScrolls.length) {
				gsap.to(introScrolls, { autoAlpha: 1, duration: 0.8, ease: 'power2.out', delay: 1.1 });
			}
		} else {
			if (heroImg) gsap.fromTo(heroImg, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.7, ease: 'power1.out', delay: 0.1 });
			if (heroCanImg) gsap.fromTo(heroCanImg, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5, delay: 0.3 });
			if (heroDecoL) { gsap.set(heroDecoL, { autoAlpha: 0 }); gsap.to(heroDecoL, { autoAlpha: 1, duration: 0.5, delay: 0.5 }); }
			if (heroDecoR) { gsap.set(heroDecoR, { autoAlpha: 0 }); gsap.to(heroDecoR, { autoAlpha: 1, duration: 0.5, delay: 0.55 }); }
			if (introEyebrow) {
				gsap.to(introEyebrow, { autoAlpha: 1, y: 0, duration: 0.5, ease: 'power1.out', delay: 0.65 });
			}
			if (introScrolls.length) {
				gsap.to(introScrolls, { autoAlpha: 1, duration: 0.5, ease: 'power1.out', delay: 0.65 });
			}
		}
	}

	// ===========================================================
	// 2. INTRO (heading + description only — eyebrow handled above)
	// ===========================================================
	const introContainer = $('.h-intro__container');
	if (introContainer) {
		const introHeading = $('h1', introContainer);
		const introDesc = $('.h-intro__description', introContainer);
		const remaining = [introHeading, introDesc].filter(Boolean);
		if (remaining.length) reveal(introContainer, remaining, { stagger: 0.12 });
	}

	// ===========================================================
	// 3. PRODUCT (.h-product__can has CSS rotate — fade only)
	// ===========================================================
	const product = $('.h-product');
	if (product) {
		const productHero = $('.h-product__hero', product);
		fadeOnly($('.h-product__can', product), { trigger: productHero, start: 'top 80%' });
		fadeOnly($('.h-product__decoration--butterfly-pea', product), { delay: 0.2, trigger: productHero, start: 'top 80%' });
		fadeOnly($('.h-product__decoration--orange-peel', product), { delay: 0.35, trigger: productHero, start: 'top 80%' });

		const productInfo = $('.h-product__info', product);
		if (productInfo) {
			reveal(productInfo, $$(
				'.h-product__eyebrow, .h-product__heading, .h-product__price, .h-product__pack-label, .h-product__variants, .h-product__actions',
				productInfo
			));
		}
	}

	// ===========================================================
	// 4. STORY SECTIONS (appears twice)
	// ===========================================================
	$$('.h-story').forEach((section) => {
		reveal($('.h-story__text', section));
		revealArch($('.h-story__arch', section));

		const tagline = $('.h-story__tagline-img', section);
		if (tagline) {
			gsap.set(tagline, { autoAlpha: 0, y: MODE === 'lite' ? 0 : 16 });
			ScrollTrigger.create({
				trigger: tagline, start: 'top 85%', once: true,
				onEnter: () => gsap.to(tagline, { autoAlpha: 1, y: 0, duration: MODE === 'lite' ? 0.5 : 0.9, ease: 'power2.out' }),
			});
		}

		$$('.h-story__decoration', section).forEach((deco) => fadeOnly(deco));
	});

	// ===========================================================
	// 5. BOTANICALS
	// ===========================================================
	const botanicals = $('.h-botanicals');
	if (botanicals) {
		const heading = $('.h-botanicals__heading', botanicals);
		const cards = $$('.h-botanicals__card', botanicals);
		const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

		const rotations = {
			'liquorice-root': -6, 'lemon-myrtle': 7, 'butterfly-pea': 5,
			'chirata-root': -4, 'quassia': 4, 'black-tea': -3,
			'gentian-root': 7, 'citrus-peel': 7,
		};
		function getRotation(card) {
			for (const [key, deg] of Object.entries(rotations)) {
				if (card.classList.contains(`h-botanicals__card--${key}`)) return deg;
			}
			return 0;
		}

		if (isDesktop && cards.length && MODE === 'full') {
			gsap.set(heading, { autoAlpha: 0, y: 16 });
			cards.forEach((card) => gsap.set(card, { autoAlpha: 0, scale: 0, rotation: 0 }));

			ScrollTrigger.create({
				trigger: botanicals, start: 'top 60%', once: true,
				onEnter: () => {
					gsap.to(heading, { autoAlpha: 1, y: 0, duration: 0.6, ease: 'power2.out' });
					cards.slice(0, 4).forEach((card, i) => {
						gsap.to(card, {
							autoAlpha: 1, scale: 1, rotation: getRotation(card),
							duration: 0.6, ease: 'back.out(1.5)', delay: 0.25 + i * 0.07,
						});
					});
					cards.slice(4).forEach((card, i) => {
						gsap.to(card, {
							autoAlpha: 1, scale: 1, rotation: getRotation(card),
							duration: 0.6, ease: 'back.out(1.5)', delay: 0.5 + i * 0.07,
						});
					});
				},
			});

		} else if (isDesktop && cards.length) {
			if (heading) gsap.set(heading, { autoAlpha: 0 });
			cards.forEach((card) => gsap.set(card, { autoAlpha: 0 }));
			ScrollTrigger.create({
				trigger: botanicals, start: 'top 70%', once: true,
				onEnter: () => {
					if (heading) gsap.to(heading, { autoAlpha: 1, duration: 0.5, ease: 'power1.out' });
					gsap.to(cards, { autoAlpha: 1, duration: 0.5, stagger: 0.06, delay: 0.2, ease: 'power1.out' });
				},
			});

		} else {
			if (heading) {
				gsap.set(heading, { autoAlpha: 0, y: MODE === 'lite' ? 0 : 20 });
				ScrollTrigger.create({
					trigger: botanicals, start: 'top 75%', once: true,
					onEnter: () => gsap.to(heading, { autoAlpha: 1, y: 0, duration: 0.7, ease: 'power2.out' }),
				});
			}
			if (cards.length) {
				gsap.set(cards, { autoAlpha: 0, y: MODE === 'lite' ? 0 : 24 });
				ScrollTrigger.create({
					trigger: botanicals, start: 'top 70%', once: true,
					onEnter: () => gsap.to(cards, {
						autoAlpha: 1, y: 0,
						duration: MODE === 'lite' ? 0.5 : 0.65,
						stagger: 0.07,
						ease: MODE === 'lite' ? 'power1.out' : 'back.out(1.2)',
					}),
				});
			}
		}
	}

	// ===========================================================
	// 6. TASTING NOTES
	// ===========================================================
	const tasting = $('.h-tasting');
	if (tasting) {
		reveal($('.h-tasting__text', tasting));
		revealArch($('.h-tasting__arch', tasting));
		fadeOnly($('.h-tasting__decoration', tasting));
	}

	// ===========================================================
	// 7. QUOTE
	// ===========================================================
	const quote = $('.h-quote');
	if (quote) {
		const quoteText = $('.h-quote__text', quote);
		const quoteAttrib = $('.h-quote__attribution', quote);
		const targets = [quoteText, quoteAttrib].filter(Boolean);

		if (targets.length) {
			gsap.set(targets, { autoAlpha: 0, y: MODE === 'lite' ? 0 : 20 });
			ScrollTrigger.create({
				trigger: quote, start: 'top 78%', once: true,
				onEnter: () => {
					const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });
					if (quoteText) tl.to(quoteText, { autoAlpha: 1, y: 0, duration: MODE === 'lite' ? 0.6 : 1 });
					if (quoteAttrib) tl.to(quoteAttrib, { autoAlpha: 1, y: 0, duration: MODE === 'lite' ? 0.4 : 0.6 }, '-=0.3');
				},
			});
		}
	}

	// ===========================================================
	// 8. SPRITZ
	// ===========================================================
	const spritz = $('.h-spritz');
	if (spritz) {
		reveal($('.h-spritz__text', spritz));
		revealArch($('.h-spritz__arch', spritz));
		fadeOnly($('.h-spritz__decoration', spritz));
	}

	// ===========================================================
	// 9. FAQ
	// ===========================================================
	const faq = $('.h-faq');
	if (faq) {
		const faqInner = $('.h-faq__inner', faq);
		if (faqInner) reveal(faqInner, $$('.h-faq__eyebrow, .h-faq__heading', faqInner));

		const accordions = $$('.accordion', faq);
		if (accordions.length) {
			gsap.set(accordions, { autoAlpha: 0, y: MODE === 'lite' ? 0 : 12 });
			ScrollTrigger.create({
				trigger: $('.h-faq__content', faq) || faq, start: 'top 80%', once: true,
				onEnter: () => gsap.to(accordions, {
					autoAlpha: 1, y: 0,
					duration: MODE === 'lite' ? 0.4 : 0.6,
					stagger: 0.06, ease: 'power2.out',
				}),
			});
		}

		$$('.h-faq__decoration', faq).forEach((deco) => fadeOnly(deco));
	}

	// ===========================================================
	// BUTTONS
	// ===========================================================
	enhanceButtons('.h-product__atc, .h-spritz__cta, .h-story__video-btn');

	// ===========================================================
	// SCROLLTRIGGER REFRESH
	// ===========================================================
	window.addEventListener('load', () => ScrollTrigger.refresh());
	if (document?.fonts?.ready) {
		document.fonts.ready.then(() => ScrollTrigger.refresh()).catch(() => { });
	}
})();
